import type { QueuePayloadFraming } from '@pink/shared';

import { bytesToBase64 } from './bytes-to-base64.js';

/**
 * Klartext-Payload, der an den Tenant verschlüsselt wird.
 *
 * Felder werden in AFO-009 vom Submit-Handler befüllt. `patientPublicKey`
 * ist die SPKI/Base64-Variante aus `generatePatientKeypair`.
 */
export interface PatientPayload {
  readonly dob: string;
  /**
   * FHIR-Appointment-ID des Termins. Stammt aus dem SMS-Token (der
   * Code-Erzeuger legt sie dort ab); das Frontend reicht sie nur durch.
   * Downstream übergibt cloud-connect sie dem doctolib-gateway als
   * Lookup-Schlüssel (`absageId`).
   */
  readonly appointmentId: string;
  readonly timeSlot: string;
  readonly location: string;
  readonly patientPublicKey: string;
}

const IV_BYTE_LENGTH = 12;
const AES_KEY_BITS = 256;

const AES_KEY_PARAMS: AesKeyGenParams = {
  name: 'AES-GCM',
  length: AES_KEY_BITS,
};

const WRAP_ALGORITHM: RsaOaepParams = { name: 'RSA-OAEP' };

/**
 * Hybrid-Verschlüsselung des Payloads für `POST /api/cancel`.
 *
 * Schritte: JSON serialisieren → AES-256-GCM-Sessionkey generieren →
 * mit 12-Byte-IV verschlüsseln → AES-Key per RSA-OAEP-`wrapKey` mit dem
 * verifizierten Tenant-Key wrappen → Base64 codieren.
 *
 * Hinweis: AES-Sessionschlüssel MUSS pro Aufruf neu generiert werden.
 * Wiederverwendung mit zufälligem IV verletzt AES-GCM-Sicherheit.
 *
 * Audit-Notiz (DEV-013 LOW-1): Der AES-Sessionkey wird zwingend mit
 * `extractable: true` generiert, weil `wrapKey('raw', ...)` das erfordert.
 * Er lebt nur im Funktions-Scope und wird nach Rückkehr GC-frei — Memory-Wipe
 * ist in JS nicht deterministisch möglich. Bei Refactoring den Extractable-
 * Flag NICHT auf `false` setzen, sonst scheitert `wrapKey`.
 *
 * @param plain Klartext-Payload (`PatientPayload`).
 * @param tenantPublicKey Verifizierter Tenant-Key aus dem Pinning-Gate
 *   (DEV-012), importiert mit `usages: ['wrapKey']`.
 */
/**
 * Hybrid-Verschlüsselung der Patient-Klartextdaten.
 *
 * Rückgabe ist das `QueuePayloadFraming`-Triple (`iv`, `encryptedKey`,
 * `ciphertext`); der Submit-Handler ruft `packQueuePayload(...)` aus
 * `@pink/shared` darauf auf, um daraus den einzelnen Wire-Payload-String
 * für `CreateItemRequest.payload` zu bauen.
 */
export async function encryptPayload(
  plain: PatientPayload,
  tenantPublicKey: CryptoKey,
): Promise<QueuePayloadFraming> {
  const payloadBytes = new TextEncoder().encode(JSON.stringify(plain));
  const aesKey = await crypto.subtle.generateKey(AES_KEY_PARAMS, true, ['encrypt']);
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTE_LENGTH));
  const ciphertextBuffer = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    aesKey,
    payloadBytes,
  );
  const encryptedKeyBuffer = await crypto.subtle.wrapKey(
    'raw',
    aesKey,
    tenantPublicKey,
    WRAP_ALGORITHM,
  );
  return {
    ciphertext: bytesToBase64(new Uint8Array(ciphertextBuffer)),
    encryptedKey: bytesToBase64(new Uint8Array(encryptedKeyBuffer)),
    iv: bytesToBase64(iv),
  };
}
